import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import {
  LinkIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ArrowDownTrayIcon,
  CloudArrowUpIcon,
  CalendarIcon,
  ChartBarIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { publicLinksApi } from '../api/public-links';
import { Spinner } from '../components/ui/Spinner';
import { useToast } from '../components/ui/Toaster';
import { formatShortDate } from '../utils/dateFormat';
import clsx from 'clsx';

interface PublicLink {
  id: string;
  linkType: 'download' | 'upload';
  token: string;
  fileId: string | null;
  folderId: string | null;
  fileName?: string;
  folderName?: string;
  hasPassword: boolean;
  maxDownloads: number | null;
  currentDownloads: number;
  maxUploads: number | null;
  currentUploads: number;
  expiresAt: string | null;
  isActive: boolean;
  createdAt: string;
  lastAccessedAt: string | null;
}

function LinkItem({
  link,
  onDelete,
  onToggle,
}: {
  link: PublicLink;
  onDelete: (id: string) => void;
  onToggle: (id: string, active: boolean) => void;
}) {
  const { t } = useTranslation('sharedLinks');
  const [copied, setCopied] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [stats, setStats] = useState<{
    totalDownloads: number;
    totalUploads: number;
    recentAccess: Array<{
      id: string;
      action: string;
      ipAddress: string | null;
      createdAt: string;
    }>;
  } | null>(null);
  const toast = useToast();

  const fullUrl = `${window.location.origin}/${link.linkType === 'download' ? 'share' : 'upload'}/${link.token}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('toast.copyFailed'));
    }
  };

  const loadStats = async () => {
    if (stats) {
      setShowStats(!showStats);
      return;
    }
    try {
      const data = await publicLinksApi.getStats(link.id);
      setStats(data);
      setShowStats(true);
    } catch {
      toast.error(t('toast.statsFailed'));
    }
  };

  const isExpired = link.expiresAt && new Date(link.expiresAt) < new Date();

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={clsx(
        'bg-white dark:bg-slate-800 rounded-xl border p-4 transition-all',
        link.isActive && !isExpired
          ? 'border-gray-200 dark:border-slate-700'
          : 'border-gray-200 dark:border-slate-700 opacity-60'
      )}
    >
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div
          className={clsx(
            'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
            link.linkType === 'download'
              ? 'bg-blue-100 dark:bg-blue-900/30'
              : 'bg-green-100 dark:bg-green-900/30'
          )}
        >
          {link.linkType === 'download' ? (
            <ArrowDownTrayIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          ) : (
            <CloudArrowUpIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium text-gray-900 dark:text-white truncate">
              {link.fileName || link.folderName || t('untitled')}
            </p>
            {link.hasPassword && (
              <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-xs rounded-full">
                {t('badges.protected')}
              </span>
            )}
            {isExpired && (
              <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs rounded-full">
                {t('badges.expired')}
              </span>
            )}
            {!link.isActive && (
              <span className="px-2 py-0.5 bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400 text-xs rounded-full">
                {t('badges.disabled')}
              </span>
            )}
          </div>

          <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 dark:text-slate-400">
            <span className="flex items-center gap-1">
              <CalendarIcon className="w-3.5 h-3.5" />
              {formatShortDate(link.createdAt)}
            </span>
            {link.linkType === 'download' ? (
              <span>
                {link.currentDownloads}
                {link.maxDownloads ? ` / ${link.maxDownloads}` : ''} {t('stats.downloads')}
              </span>
            ) : (
              <span>
                {link.currentUploads}
                {link.maxUploads ? ` / ${link.maxUploads}` : ''} {t('stats.uploads')}
              </span>
            )}
            {link.expiresAt && !isExpired && (
              <span>{t('expires', { date: formatShortDate(link.expiresAt) })}</span>
            )}
          </div>

          {/* URL */}
          <div className="flex items-center gap-2 mt-3">
            <code className="flex-1 px-3 py-1.5 bg-gray-100 dark:bg-slate-700 rounded text-xs text-gray-600 dark:text-slate-300 truncate">
              {fullUrl}
            </code>
            <button
              onClick={handleCopy}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-colors"
              title={t('actions.copyLink')}
            >
              {copied ? (
                <CheckIcon className="w-4 h-4 text-green-500" />
              ) : (
                <ClipboardDocumentIcon className="w-4 h-4 text-gray-400" />
              )}
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={loadStats}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            title={t('actions.viewStats')}
          >
            <ChartBarIcon className="w-4 h-4 text-gray-400" />
          </button>
          <button
            onClick={() => onToggle(link.id, !link.isActive)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            title={link.isActive ? t('actions.disableLink') : t('actions.enableLink')}
          >
            <EyeIcon
              className={clsx(
                'w-4 h-4',
                link.isActive ? 'text-green-500' : 'text-gray-400'
              )}
            />
          </button>
          <button
            onClick={() => onDelete(link.id)}
            className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            title={t('actions.deleteLink')}
          >
            <TrashIcon className="w-4 h-4 text-red-500" />
          </button>
        </div>
      </div>

      {/* Stats panel */}
      <AnimatePresence>
        {showStats && stats && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700"
          >
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.totalDownloads}
                </p>
                <p className="text-sm text-gray-500 dark:text-slate-400">{t('stats.totalDownloads')}</p>
              </div>
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.totalUploads}
                </p>
                <p className="text-sm text-gray-500 dark:text-slate-400">{t('stats.totalUploads')}</p>
              </div>
            </div>

            {stats.recentAccess.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                  {t('stats.recentActivity')}
                </p>
                <div className="space-y-1">
                  {stats.recentAccess.slice(0, 5).map((access) => (
                    <div
                      key={access.id}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="text-gray-600 dark:text-slate-400">
                        {access.action === 'download' ? t('stats.downloaded') : t('stats.uploaded')}
                        {access.ipAddress && ` ${t('stats.from')} ${access.ipAddress}`}
                      </span>
                      <span className="text-gray-400 dark:text-slate-500">
                        {formatShortDate(access.createdAt)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export function SharedLinksPage() {
  const { t } = useTranslation('sharedLinks');
  const [links, setLinks] = useState<PublicLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();

  const loadLinks = useCallback(async () => {
    try {
      const { links: data } = await publicLinksApi.list();
      setLinks(data);
    } catch {
      toast.error(t('toast.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [toast, t]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const handleDelete = async (id: string) => {
    try {
      await publicLinksApi.delete(id);
      setLinks((prev) => prev.filter((l) => l.id !== id));
      toast.success(t('toast.deleted'));
    } catch {
      toast.error(t('toast.deleteFailed'));
    }
  };

  const handleToggle = async (id: string, isActive: boolean) => {
    try {
      await publicLinksApi.update(id, { isActive });
      setLinks((prev) =>
        prev.map((l) => (l.id === id ? { ...l, isActive } : l))
      );
      toast.success(isActive ? t('toast.enabled') : t('toast.disabled'));
    } catch {
      toast.error(t('toast.updateFailed'));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('title')}</h1>
          <p className="text-gray-500 dark:text-slate-400 mt-1">
            {t('subtitle')}
          </p>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : links.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center justify-center py-20 text-center"
        >
          <div className="w-20 h-20 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
            <LinkIcon className="w-10 h-10 text-gray-400 dark:text-slate-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
            {t('empty.title')}
          </h3>
          <p className="text-gray-500 dark:text-slate-400 max-w-sm">
            {t('empty.description')}
          </p>
        </motion.div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence>
            {links.map((link) => (
              <LinkItem
                key={link.id}
                link={link}
                onDelete={handleDelete}
                onToggle={handleToggle}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
